<template>
  <Modal :show="isVisible" @close="handleClose" modal-size="sm:my-8 sm:w-full sm:max-w-3xl sm:p-6"
    :prevent-backdrop-close="true">
    <template #header>
      <div class="flex items-center gap-2">
        <span class="text-xl font-bold text-gray-900 dark:text-gray-100">
          {{ announcement?.title }}
        </span>
      </div>
    </template>

    <template #body>
      <div class="space-y-6 max-h-[500px] overflow-y-auto announcement-list">
        <div class="announcement-content prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
          v-html="announcement?.content" />
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between w-full pl-3">
        <!-- 左侧：今日不再弹出 checkbox -->
        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="dismissToday" type="checkbox"
            class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-300 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
          <span class="text-sm text-gray-700 dark:text-gray-300">今日不再弹出</span>
        </label>

        <!-- 右侧：操作按钮 -->
        <div class="flex items-center gap-3">
          <button
            class="cancel-btn text-sm font-medium"
            @click="handleViewMore">
            查看更多
          </button>
          <button
            class="primary-btn text-sm px-6 font-medium"
            @click="handleConfirm">
            我知道了
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
/**
 * 公告预览弹窗组件
 *
 * @description 用于预览公告在用户端的显示效果（完全模拟 home 端的样式）
 * @author AI Assistant
 * @created 2025-12-05
 * @updated 2025-12-05 - 添加"今日不再弹出"和"查看更多"功能（仅用于预览，不实际执行）
 */

import { ref } from 'vue'
import Modal from '@/components/ui/Modal.vue'
import type { Announcement } from '@/types/announcement'

interface Props {
  /** 公告数据 */
  announcement: Announcement | null
}

interface Emits {
  /** 关闭事件 */
  (e: 'close'): void
}

defineProps<Props>()
const emit = defineEmits<Emits>()

/** 显示状态 */
const isVisible = ref(false)

/** 是否勾选"今日不再弹出"（仅用于预览） */
const dismissToday = ref(false)

/**
 * 打开 Modal
 */
const open = () => {
  isVisible.value = true
  // 重置 checkbox 状态
  dismissToday.value = false
}

/**
 * 关闭 Modal
 */
const close = () => {
  isVisible.value = false
  emit('close')
}

/**
 * 处理关闭（用于 Modal 的 @close 事件）
 */
const handleClose = () => {
  close()
}

/**
 * 处理"查看更多"按钮点击（预览模式，仅关闭弹窗）
 */
const handleViewMore = () => {
  console.log('[预览模式] 用户点击了"查看更多"按钮')
  close()
}

/**
 * 处理"我知道了"按钮点击（预览模式，仅关闭弹窗）
 */
const handleConfirm = () => {
  if (dismissToday.value) {
    console.log('[预览模式] 用户勾选了"今日不再弹出"')
  } else {
    console.log('[预览模式] 用户未勾选"今日不再弹出"')
  }
  close()
}

// 暴露方法给父组件
defineExpose({
  open,
  close,
})
</script>

<style scoped>
/**
 * 公告内容样式
 */
.announcement-content {
  font-size: 15px;
  line-height: 1.7;
}

/**
 * 公告列表滚动条样式
 */
.announcement-list::-webkit-scrollbar {
  width: 6px;
}

.announcement-list::-webkit-scrollbar-track {
  background: transparent;
}

.announcement-list::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.dark .announcement-list::-webkit-scrollbar-thumb {
  background: #4a5568;
}

.announcement-list::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.dark .announcement-list::-webkit-scrollbar-thumb:hover {
  background: #718096;
}
</style>
